import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';

interface ReportCard {
  id?: string;
  title: string;
  icon?: string;
  navigateTo?: string;
  externalUrl?: string;
  disabled?: boolean;
}

interface ReportsConfig {
  title?: string;
  dataUrl?: string;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
})
export class ReportsComponent implements OnInit {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private readonly DEFAULT_CONFIG_FILE = 'reports-config.json';
  private readonly DEFAULT_DATA_URL = 'assets/mock-data/reports.json';
  private readonly DEFAULT_ICON = 'dashboard';

  title = 'Reports';
  private dataUrl = this.DEFAULT_DATA_URL;

  /** The grid renders exactly one card per item returned by the dummy API,
   *  so the number of boxes is driven entirely by the data, not hard-coded. */
  cards: ReportCard[] = [];
  isLoading = true;

  ngOnInit(): void {
    const configFile =
      (this.route.snapshot.data?.['configFile'] as string)?.trim() ||
      this.DEFAULT_CONFIG_FILE;

    this.http
      .get<ReportsConfig>(`assets/dashboard-config/${configFile}`)
      .subscribe({
        next: (config) => {
          this.applyConfig(config);
          this.loadCards();
        },
        error: () => {
          this.applyConfig(null);
          this.loadCards();
        },
      });
  }

  private applyConfig(config: ReportsConfig | null): void {
    this.title = config?.title?.trim() || this.title;
    this.dataUrl = config?.dataUrl?.trim() || this.dataUrl;
  }

  /** Hits the dummy API (a mock JSON file) — its length decides how many
   *  boxes appear on screen. */
  private loadCards(): void {
    this.http.get<ReportCard[]>(this.dataUrl).subscribe({
      next: (data) => {
        this.cards = Array.isArray(data) ? data : [];
        this.isLoading = false;
      },
      error: () => {
        this.cards = [];
        this.isLoading = false;
      },
    });
  }

  iconFor(card: ReportCard): string {
    return (card.icon || '').trim() || this.DEFAULT_ICON;
  }

  onCardClick(card: ReportCard): void {
    if (card?.disabled) return;

    const route = (card.navigateTo || '').trim();
    if (route) {
      this.router.navigateByUrl(route);
      return;
    }

    const externalUrl = (card.externalUrl || '').trim();
    if (externalUrl) {
      window.open(externalUrl, '_self');
    }
  }

  onCardKeydown(event: KeyboardEvent, card: ReportCard): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onCardClick(card);
  }

  trackByCard(index: number, card: ReportCard): string {
    return card.id ?? card.title ?? String(index);
  }
}
